export const settings = {
  title: "Settings",
  description: "Your own preferences, and how your organization is set up.",
  groups: { you: "You", org: "Organization" },
  nav: {
    profile: "Profile",
    preferences: "Preferences",
    notifications: "Notifications",
    general: "General",
    channels: "Channels",
    team: "Team & roles",
    ai: "AI & knowledge",
    plan: "Plan & billing",
    api: "API & integrations",
    data: "Data & privacy",
  },
  hints: {
    profile: "Your name, sign-in email and role.",
    preferences: "Language, theme, density and the Ask button.",
    notifications: "What reaches you, and where.",
    general: "Your organization and its calendar.",
    channels: "Connected Instagram accounts and what each channel can do.",
    team: "Who can see and act in this organization.",
    ai: "AI replies, tags, AI categories and smart search.",
    plan: "Your plan, usage against its limits, and billing.",
    api: "The public API and outgoing events.",
    data: "How long data is kept, incidents and legal pages.",
  },
  readOnly: "Only owners and admins can change these settings.",
  profile: {
    title: "Your details",
    hint: "How you appear to your team.",
    name: "Name",
    email: "Email",
    emailHint: "Your sign-in email.",
    nameHint: "Ask an owner or admin if your name needs to change.",
    language: "Preferred language",
    organization: "Organization",
    role: "Your role",
    photo: "Photo",
    photoHint: "Your initials, until photos arrive.",
    guide: "Getting started guide",
    guideHint: "The checklist on Home that walks you through your first replies, rules and posts.",
    guideShow: "Show the getting started guide",
    guideShown: "The guide is back on Home",
    guideVisible: "The guide is showing on Home.",
  },
  general: {
    org: "Organization",
    orgHint: "The organization you're working in now.",
    name: "Name",
    yourRole: "Your role",
    members: "Members",
    plan: "Plan",
    renameHint: "To rename your organization, contact Malfak.",
    manageTeam: "Manage team",
    viewPlan: "View plan",
    calendar: "Calendar",
    calendarHint: "Occasions, holidays and reminders shown on your content calendar.",
  },
  aiPage: {
    accounts: "AI replies per account",
    accountsHint: "When on, AI reads each new comment and message, tags it and suggests a reply. Nothing is sent without you or a rule you turned on.",
    noAccounts: "Connect an Instagram account to switch on AI replies.",
    connect: "Go to Channels",
    tags: "Tags & AI categories",
    more: "More AI tools",
    rules: "Rules",
    rulesHint: "Decide what happens automatically, and when AI only suggests.",
    agents: "AI agents",
    agentsHint: "Personas that answer in your voice, with their own limits.",
  },
  team: {
    title: "Team & roles",
    hint: "Who can see and act in this organization.",
    invite: "Invite teammate",
    inviteTitle: "Invite a teammate",
    inviteHint: "They'll get an email with a link to join {{org}}.",
    inviteEmail: "Email address",
    inviteRole: "Role",
    inviteSend: "Send invite",
    inviteSent: "Invites are coming soon. We noted {{email}}.",
    member: "Member",
    role: "Role",
    joined: "Joined",
    you: "You",
    ownerLocked: "The owner's role can't be changed.",
    selfLocked: "You can't change your own role.",
    roleSoon: "Changing roles is coming soon.",
    count: "{{n}} members",
    roleHints: {
      owner: "Everything, including billing and deleting the organization.",
      admin: "Manage team, channels and automations.",
      editor: "Reply, publish and edit rules.",
      viewer: "Read-only access to the inbox and analytics.",
    },
  },
  channels: {
    title: "Channels",
    hint: "Connected accounts, their live updates and what each channel supports.",
    connected: "Connected accounts",
    connect: "Connect Instagram",
    connecting: "Connecting…",
    connectedToast: "Instagram connected",
    noAccounts: "No channels connected yet",
    noAccountsHint: "Connect Instagram to bring comments and DMs into Malfak.",
    status: "Status",
    webhook: "Live updates",
    subscribed: "Subscribed",
    notSubscribed: "Not subscribed",
    fields: "Fields",
    lastSync: "Last sync",
    never: "Never",
    connectedSince: "Connected",
    followers: "Followers",
    syncNow: "Sync now",
    syncing: "Syncing @{{user}}…",
    synced: "@{{user}} is up to date",
    ai: "AI replies",
    aiHint: "Classify comments and draft replies for this account.",
    aiOn: "AI turned on for @{{user}}",
    aiOff: "AI turned off for @{{user}}",
    disconnect: "Disconnect",
    disconnectTitle: "Disconnect @{{user}}?",
    disconnectBody: "Malfak stops receiving comments and messages from this account. Your history stays, and you can reconnect any time.",
    disconnected: "@{{user}} disconnected",
    reconnect: "Reconnect",
    matrix: "What each channel can do",
    matrixHint: "Malfak adapts every screen to these limits, so nothing fails at the platform.",
    capability: "Capability",
    available: "Available",
    comingSoon: "Coming soon",
    rows: {
      publish: "Publishing",
      carousel: "Carousel items",
      quota: "Posts per 24h",
      commentReply: "Reply to comments",
      commentHide: "Hide comments",
      privateReply: "Private reply to a comment",
      dm: "Direct messages",
      dmWindow: "Free-reply window",
      outsideWindow: "After the window",
      quickReplies: "Quick replies & buttons",
      insights: "Insights",
    },
    outside: {
      none: "No messages",
      message_tag: "Message tags",
      template: "Approved templates",
    },
  },
  notifications: {
    title: "Notifications",
    hint: "Choose what reaches you inside Malfak. Saved on this device.",
    soon: "Email, Slack and Telegram are coming soon.",
    event: "Event",
    saved: "Notification preferences saved",
    events: {
      approvals: "Replies waiting for approval",
      crisis: "Negative comment spike",
      newMessage: "New direct message",
      assigned: "Assigned to me",
      publishFailed: "A post failed to publish",
      published: "A scheduled post went live",
      batchDone: "Bulk action finished",
      weeklyReport: "Weekly performance report",
    },
    channels: {
      in_app: "In-app",
      email: "Email",
      slack: "Slack",
      telegram: "Telegram",
    },
    connectHint: "Slack and Telegram deliveries start once those integrations are connected.",
  },
  appearance: {
    title: "Appearance",
    hint: "Make Malfak comfortable to work in.",
    theme: "Theme",
    themeHint: "System follows your device and switches automatically at night.",
    system: "System",
    light: "Light",
    dark: "Dark",
    language: "Language",
    languageHint: "The whole interface, dates and numbers follow this choice.",
    density: "Density",
    densityHint: "Compact fits more rows in the inbox and tables.",
    comfortable: "Comfortable",
    compact: "Compact",
    ask: "Ask button",
    askHint: "The assistant always opens from Ask in the top bar or with ⌘J.",
    floating: "Also show a floating Ask button",
    floatingHint: "A round button at the bottom of every page.",
  },
  api: {
    title: "API & integrations",
    hint: "Build on Malfak with the public API and outgoing webhooks.",
    keys: "API keys",
    keysHint: "Keys act on behalf of this organization. Treat them like passwords.",
    name: "Name",
    key: "Key",
    created: "Created",
    lastUsed: "Last used",
    createKey: "Create key",
    soon: "Public API access is coming soon.",
    sample: "Example key (disabled)",
    webhooks: "Outgoing webhooks",
    webhooksHint: "Send Malfak events to Zapier, Make, n8n or your own server.",
    endpoint: "Your Malfak events endpoint",
    copy: "Copy",
    copied: "Copied to clipboard",
    docs: "API reference",
  },
  knowledge: {
    title: "Smart search & Assistant knowledge",
    hint: "Comments, DMs and captions are indexed by meaning for smart search and the Assistant.",
    state: { ok: "Healthy", degraded: "Degraded", unavailable: "Unavailable" },
    datasets: { comments: "Comments, DMs & captions", brand: "Brand documents" },
    indexed: "{{n}} indexed",
    indexing: "{{n}} indexing",
    failed: "{{n}} failed",
    notChecked: "Not checked yet",
    failedTitle_one: "Documents that failed to index: {{count}}",
    failedTitle_other: "Documents that failed to index: {{count}}",
    failedHint: "They can't be found by smart search or the Assistant. Re-index to submit them again.",
    lastError: "Last error: {{error}}",
    reindex: "Re-index",
    reindexStarted: "Re-index started. Follow it in Jobs.",
    adminOnly: "Ask an owner or admin to re-index.",
  },
};
